import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, LogOut, Lock, Calendar, CheckCircle2, UserX, XCircle,
  RefreshCw, User, Clock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import BookingsCalendarView from '../components/BookingsCalendarView';
import { List, LayoutGrid } from 'lucide-react';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import {
  adminListBookings, adminMarkAttended, adminMarkNoShow, adminAdminCancelBooking,
} from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHuDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('hu-HU'); } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:   'bg-amber-500/15 text-amber-300',
    attended:  'bg-emerald-500/15 text-emerald-300',
    no_show:   'bg-red-500/15 text-red-300',
    cancelled: 'bg-gray-500/15 text-gray-400',
  };
  const cls = map[status] || 'bg-gray-500/15 text-gray-300';
  const lbl = {
    pending:'Függőben', attended:'Megjelent', no_show:'Nem jelent meg', cancelled:'Lemondva',
  }[status] || status;
  return (
    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${cls}`}>
      {lbl}
    </span>
  );
};

const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminListBookings(key.trim());
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
    } catch (_) { toast.error('Érvénytelen admin kulcs.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form onSubmit={submit} data-testid="admin-bookings-login" className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl">
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <Calendar className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Foglalások</h1>
        <Input data-testid="admin-bookings-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" />
        <Button data-testid="admin-bookings-login-btn" type="submit" disabled={busy || !key.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
          {busy ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

const AdminBookingsPage = () => {
  useDocumentMeta({ title: 'Foglalások | Admin | Orosz Coaching' });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('upcoming'); // upcoming | pending | attended | no_show | cancelled | all
  const [viewMode, setViewMode] = useState('list'); // list | calendar
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const opts = filter === 'upcoming'
        ? { upcoming: true }
        : filter === 'all' ? {} : { status: filter };
      const data = await adminListBookings(adminKey, opts);
      setRows(data.bookings || []);
      setCounts(data.counts || {});
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Lejárt admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error(err?.response?.data?.detail || 'Betöltési hiba.');
      }
    } finally { setLoading(false); }
  }, [adminKey, filter]);

  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);

  const doAction = async (id, kind) => {
    setBusy(id);
    try {
      if (kind === 'attended') await adminMarkAttended(adminKey, id);
      else if (kind === 'no_show') await adminMarkNoShow(adminKey, id);
      else if (kind === 'cancel') await adminAdminCancelBooking(adminKey, id, 'admin_cancelled');
      toast.success('Frissítve.');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Sikertelen művelet.'); }
    finally { setBusy(null); }
  };

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;

  const filters = [
    { key: 'upcoming',  label: 'Közelgő' },
    { key: 'pending',   label: `Függőben (${counts.pending || 0})` },
    { key: 'attended',  label: `Megjelent (${counts.attended || 0})` },
    { key: 'no_show',   label: `Nem jelent meg (${counts.no_show || 0})` },
    { key: 'cancelled', label: `Lemondva (${counts.cancelled || 0})` },
    { key: 'all',       label: 'Összes' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <Calendar className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Foglalások</h1>
              <p className="text-xs text-gray-400">Kliens edzés-foglalások kezelése (megjelent / no-show / lemondás).</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center rounded-full bg-gray-800 p-0.5">
              <button
                data-testid="admin-bookings-view-list"
                onClick={() => setViewMode('list')}
                className={
                  'inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ' +
                  (viewMode === 'list' ? 'bg-yellow-500 text-gray-900' : 'text-gray-300 hover:text-white')
                }
              >
                <List size={13} /> Lista
              </button>
              <button
                data-testid="admin-bookings-view-calendar"
                onClick={() => setViewMode('calendar')}
                className={
                  'inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ' +
                  (viewMode === 'calendar' ? 'bg-yellow-500 text-gray-900' : 'text-gray-300 hover:text-white')
                }
              >
                <LayoutGrid size={13} /> Naptár
              </button>
            </div>
            <Button data-testid="admin-bookings-refresh" variant="ghost" size="sm" onClick={load} className="text-gray-300 hover:text-white">
              <RefreshCw size={14} className="mr-1.5" /> Frissítés
            </Button>
            <Button data-testid="admin-bookings-logout" variant="ghost" size="sm" onClick={() => { localStorage.removeItem(ADMIN_KEY_STORAGE); setAdminKey(''); }} className="text-gray-400 hover:text-red-400">
              <LogOut size={14} />
            </Button>
          </div>
        </div>
        <div className="container mx-auto px-6 pb-3 flex flex-wrap gap-1.5">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`admin-bookings-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={
                'text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ' +
                (filter === f.key
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700')
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="animate-spin mx-auto text-yellow-500 mb-2" size={28} /><p className="text-gray-400 text-sm">Betöltés…</p></div>
        ) : viewMode === 'calendar' ? (
          <BookingsCalendarView
            bookings={rows}
            busy={busy}
            onAction={(id, kind) => doAction(id, kind)}
          />
        ) : rows.length === 0 ? (
          <div className="text-center py-24 text-gray-400">Nincs találat ebben a szűrőben.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.booking_id} data-testid={`admin-booking-row-${r.booking_id}`} className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StatusBadge status={r.status} />
                      <span className="text-sm font-bold text-white"><Clock size={12} className="inline-block mr-1 -mt-0.5 text-gray-400" />{r.slot_date} · {r.slot_time}</span>
                      <span className="text-[10px] text-gray-500 font-mono">{r.booking_id}</span>
                    </div>
                    <div className="text-sm text-gray-300 mt-1">
                      <User size={12} className="inline-block mr-1 -mt-0.5 text-gray-500" />
                      {r.user?.name || '—'} <span className="text-gray-500">· {r.user?.email}</span>
                    </div>
                    {r.pass && (
                      <div className="text-xs text-gray-500 mt-1">
                        Bérlet: {r.pass.remaining_sessions}/{r.pass.total_sessions} · lejár: {r.pass.expires_at ? new Date(r.pass.expires_at).toLocaleDateString('hu-HU') : '—'}
                      </div>
                    )}
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button data-testid={`admin-booking-cancel-${r.booking_id}`} variant="ghost" size="sm" disabled={busy === r.booking_id} onClick={() => doAction(r.booking_id, 'cancel')} className="text-gray-400 hover:text-red-400"><XCircle size={14} className="mr-1" /> Lemondás</Button>
                      <Button data-testid={`admin-booking-noshow-${r.booking_id}`} variant="outline" size="sm" disabled={busy === r.booking_id} onClick={() => doAction(r.booking_id, 'no_show')} className="border-red-500/40 text-red-300 hover:bg-red-500/10"><UserX size={14} className="mr-1" /> Nem jelent meg</Button>
                      <Button data-testid={`admin-booking-attended-${r.booking_id}`} size="sm" disabled={busy === r.booking_id} onClick={() => doAction(r.booking_id, 'attended')} className="bg-emerald-600 hover:bg-emerald-700 font-bold">
                        {busy === r.booking_id ? <><Loader2 className="animate-spin mr-1.5" size={14} /> …</> : <><CheckCircle2 size={14} className="mr-1" /> Megjelent</>}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBookingsPage;
